/**
 * Where a compte rendu de télé-expertise is actually read.
 *
 * The e-mail carries a door, never the document: a PDF in a mailbox cannot be expired, revoked, or
 * taken back from the stranger a mistyped address delivered it to. This page is that door, and the
 * date of birth is what guards it — the patient knows theirs, the physician who referred them knows
 * their patient's, and someone who received the mail by mistake knows neither.
 *
 * It is deliberately outside the application: no session, no sidebar, no navigation. The only two
 * things a visitor can do here are answer the question and read their compte rendu.
 *
 * Every refusal is shown as the sentence the API wrote, unedited. They are worded for the person
 * reading them — « rapprochez-vous du secrétariat » is an instruction someone can act on, where
 * « 403 Forbidden » is not — and rewording them here would put two sources of truth on one screen.
 */

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { Trans, useLingui } from "@lingui/macro";
import { FileText, Loader2, ShieldCheck } from "lucide-react";
import API from "@app/services/api";
import { Button } from "@app/components/ui/button";

type Etat = "chargement" | "ouvert" | "ferme";

interface Reponse {
  ok?: boolean;
  data?: { url?: string; destinataire?: string };
  detail?: string;
  error?: string;
}

/** The API answers 4xx with FastAPI's ``detail``; everything else in the app uses ``error``. */
function raison(response: Reponse | undefined, secours: string): string {
  return response?.detail || response?.error || secours;
}

export default function CompteRenduLien() {
  const { jeton } = useParams();
  const { t } = useLingui();
  const [etat, setEtat] = useState<Etat>("chargement");
  // Which door this is. The question is the same one either way, but « votre date de naissance »
  // asked of a patient and « la date de naissance du patient » asked of their physician are not the
  // same sentence, and the wrong one reads as a form written for somebody else.
  const [destinataire, setDestinataire] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [dateNaissance, setDateNaissance] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let vivant = true;
    void (async () => {
      const response = (await API.get({ path: `/compte-rendu/${jeton}` })) as Reponse;
      if (!vivant) return;
      if (response?.ok) {
        setDestinataire(response.data?.destinataire ?? null);
        setEtat("ouvert");
        return;
      }
      setEtat("ferme");
      setMessage(raison(response, t`Ce lien n'est pas valide.`));
    })();
    return () => {
      vivant = false;
    };
  }, [jeton, t]);

  const ouvrir = useCallback(async () => {
    setBusy(true);
    setMessage(null);
    const response = (await API.post({
      path: `/compte-rendu/${jeton}`,
      body: { date_naissance: dateNaissance },
    })) as Reponse;
    setBusy(false);
    const url = response?.data?.url;
    if (response?.ok && url) {
      // The presigned URL lives one minute: followed immediately, and never stored in state where a
      // second render could hand it to someone else.
      window.location.href = url;
      return;
    }
    setMessage(raison(response, t`Le compte rendu n'a pas pu être ouvert.`));
  }, [dateNaissance, jeton, t]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-md">
        <h1 className="inline-flex items-center gap-2 text-base font-semibold text-slate-900">
          <FileText className="h-5 w-5 text-primary" />
          <Trans>Compte rendu de télé-expertise</Trans>
        </h1>

        {etat === "chargement" && (
          <p className="mt-4 inline-flex items-center gap-2 text-sm text-slate-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            <Trans>Vérification du lien...</Trans>
          </p>
        )}

        {etat === "ferme" && <p className="mt-4 text-sm text-red-700">{message}</p>}

        {etat === "ouvert" && (
          <form
            className="mt-4 space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              void ouvrir();
            }}
          >
            <p className="text-sm text-slate-600">
              {destinataire === "patient" ? (
                <Trans>Pour des raisons de confidentialité, indiquez votre date de naissance.</Trans>
              ) : (
                <Trans>
                  Pour des raisons de confidentialité, indiquez la date de naissance du patient.
                </Trans>
              )}
            </p>
            <div className="space-y-1.5">
              <label htmlFor="date-naissance" className="text-sm font-medium text-slate-700">
                <Trans>Date de naissance</Trans>
              </label>
              <input
                id="date-naissance"
                type="date"
                required
                value={dateNaissance}
                onChange={(event) => setDateNaissance(event.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none"
              />
            </div>

            {message && <p className="text-sm text-red-700">{message}</p>}

            <Button
              type="submit"
              disabled={busy || !dateNaissance}
              className="w-full bg-primary hover:bg-primary/90"
            >
              {busy ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <ShieldCheck className="mr-1.5 h-4 w-4" />
              )}
              <Trans>Afficher le compte rendu</Trans>
            </Button>

            <p className="text-xs text-slate-500">
              <Trans>
                Ce document est confidentiel. Il reste hébergé par Mage-X et n'est pas envoyé en
                pièce jointe.
              </Trans>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
